// Schedule utilities for Legacy VAPA HS
// Based on 2025-2026 Bell Schedule

// A rotation day: schedules alternate between A days and B days, and an
// "A/B" period slot (e.g. "Period 1/5") resolves to a different period
// depending on which one it is.
export type ABDay = 'A' | 'B';

// A period slot value as it arrives from schedule config: either a
// human-readable label ("Period 1", "Period 1/5", "Advisory", "Lunch") or an
// already-numeric period.
export type PeriodSlot = string | number;

// Schedule configuration errors for user-facing messages
export class ScheduleConfigurationError extends Error {
  isAdminError: boolean;

  constructor(message: string, isAdminError = false) {
    super(message);
    this.name = 'ScheduleConfigurationError';
    this.isAdminError = isAdminError;
  }
}

// Convert period slot to actual period number based on A/B day
export const getPeriodNumber = (
  periodSlot: PeriodSlot,
  abDay?: ABDay | string | null
): PeriodSlot => {
  // Handle period slots that need A/B day resolution first
  if (typeof periodSlot === 'string' && periodSlot.includes('/')) {
    const match = periodSlot.match(/^Period\s+(\d+)\/(\d+)$/);
    if (match) {
      const [, aPeriod, bPeriod] = match;
      const selectedPeriod =
        abDay === 'B' ? parseInt(bPeriod) : parseInt(aPeriod);
      return selectedPeriod;
    }
  }

  // Extract number from already resolved "Period X" format
  if (typeof periodSlot === 'string' && periodSlot.startsWith('Period ')) {
    const num = parseInt(periodSlot.replace('Period ', '').trim());
    if (!isNaN(num) && num >= 1 && num <= 20) {
      return num;
    }
  }

  // Return as-is for Advisory, Lunch, or any unrecognized format
  return periodSlot;
};

// Get display name for period
export const getPeriodDisplayName = (
  periodSlot?: string | null,
  abDay?: ABDay | string | null
): string => {
  // Handle period slots that need A/B day resolution
  if (periodSlot && periodSlot.includes('/')) {
    // Extract periods from slot format like "Period 1/5" -> [1, 5]
    const match = periodSlot.match(/^Period\s+(\d+)\/(\d+)$/);
    if (match) {
      const [, aPeriod, bPeriod] = match;
      const selectedPeriod = abDay === 'B' ? bPeriod : aPeriod;
      return `Period ${selectedPeriod}`;
    }
  }

  // Return as-is for already resolved periods or special periods
  // "Period 1", "Period 2", ..., "Period 8", "Advisory", "Lunch"
  return periodSlot || 'Unknown Period';
};

// Get human-readable display name for a schedule type/variant
export const getScheduleDisplayName = (scheduleType: unknown): string => {
  if (!scheduleType || typeof scheduleType !== 'string') {
    return 'Regular Schedule';
  }
  return formatScheduleTypeName(scheduleType);
};

// Helper function to format schedule type names when no variant is found
const formatScheduleTypeName = (scheduleType: string): string => {
  if (!scheduleType) return 'Regular Schedule';

  // Handle common schedule types
  const commonTypes: Record<string, string> = {
    regular: 'Regular Schedule',
    shortened: 'Shortened Schedule',
    block: 'Block Schedule',
    assembly: 'Assembly Schedule',
    testing: 'Testing Schedule',
    'early-release': 'Early Release',
    'late-start': 'Late Start',
    'no-school': 'No School',
  };

  const lowerType = scheduleType.toLowerCase();
  if (commonTypes[lowerType]) {
    return commonTypes[lowerType];
  }

  // Format unknown types: capitalize first letter of each word, replace hyphens/underscores
  return (
    scheduleType
      .split(/[-_\s]+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ') + ' Schedule'
  );
};
